import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  UserCircle,
  Shield,
  ShieldCheck,
  Plus,
  PencilSimple,
  Trash,
  ArrowsClockwise,
  Eye,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';
import api from '../services/api';
import MfaStatusPanel from '../components/admin/MfaStatusPanel';

const UserManagementPage = () => {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [permissionCategories, setPermissionCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPermissions, setEditPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles/'),
        api.get('/roles/permissions')
      ]);
      setRoles(rolesRes.data.roles || []);
      setAllPermissions(permsRes.data.permissions || {});
      setPermissionCategories(permsRes.data.categories || {});
    } catch (error) {
      toast.error('Failed to load roles data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setEditPermissions(role.permissions || []);
    setEditModalOpen(true);
  };

  const handleTogglePermission = (permission) => {
    setEditPermissions(prev => {
      if (prev.includes(permission)) {
        return prev.filter(p => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleToggleCategory = (category, permissions) => {
    const allSelected = permissions.every(p => editPermissions.includes(p));
    
    if (allSelected) {
      setEditPermissions(prev => prev.filter(p => !permissions.includes(p)));
    } else {
      setEditPermissions(prev => [...new Set([...prev, ...permissions])]);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      await api.put(`/roles/${selectedRole.id}`, {
        permissions: editPermissions
      });
      toast.success('Role permissions updated');
      setEditModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Admin': return 'bg-red-100 text-red-700';
      case 'Office Manager': return 'bg-purple-100 text-purple-700';
      case 'Biller': return 'bg-blue-100 text-blue-700';
      case 'Viewer': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage roles and permissions
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Roles Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {roles.map((role) => (
              <Card 
                key={role.id} 
                className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleEditRole(role)}
                data-testid={`role-card-${role.name.toLowerCase().replace(' ', '-')}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge className={getRoleBadgeColor(role.name)}>
                      {role.name}
                    </Badge>
                    {role.is_system && (
                      <ShieldCheck size={16} className="text-slate-400" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-3">{role.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {role.permissions?.length || 0} permissions
                    </span>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <PencilSimple size={14} className="mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Permissions Overview Table */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Permission Matrix</CardTitle>
              <CardDescription>View which permissions each role has</CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-64">Permission</TableHead>
                    {roles.map((role) => (
                      <TableHead key={role.id} className="text-center w-32">
                        {role.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(permissionCategories).map(([category, permissions]) => (
                    <React.Fragment key={category}>
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={roles.length + 1} className="font-semibold capitalize">
                          {category.replace(/_/g, ' ')}
                        </TableCell>
                      </TableRow>
                      {permissions.map((perm) => (
                        <TableRow key={perm}>
                          <TableCell className="text-sm">
                            {allPermissions[perm] || perm}
                          </TableCell>
                          {roles.map((role) => (
                            <TableCell key={`${role.id}-${perm}`} className="text-center">
                              {role.permissions?.includes(perm) ? (
                                <CheckCircle size={18} className="inline text-emerald-500" />
                              ) : (
                                <XCircle size={18} className="inline text-slate-300" />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Mandatory MFA — per-user status + privileged reset */}
          <MfaStatusPanel />
        </>
      )}

      {/* Edit Role Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Edit Role: {selectedRole?.name}
            </DialogTitle>
            <DialogDescription>
              Toggle permissions for this role. Changes affect all users with this role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {Object.entries(permissionCategories).map(([category, permissions]) => {
              const allSelected = permissions.every(p => editPermissions.includes(p));
              const someSelected = permissions.some(p => editPermissions.includes(p));
              
              return (
                <div key={category} className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`cat-${category}`}
                      checked={allSelected}
                      onCheckedChange={() => handleToggleCategory(category, permissions)}
                      className={someSelected && !allSelected ? 'data-[state=checked]:bg-slate-400' : ''}
                    />
                    <Label 
                      htmlFor={`cat-${category}`} 
                      className="text-sm font-semibold capitalize cursor-pointer"
                    >
                      {category.replace(/_/g, ' ')}
                    </Label>
                  </div>
                  <div className="grid grid-cols-2 gap-2 ml-6">
                    {permissions.map((perm) => (
                      <div key={perm} className="flex items-center space-x-2">
                        <Checkbox
                          id={perm}
                          checked={editPermissions.includes(perm)}
                          onCheckedChange={() => handleTogglePermission(perm)}
                        />
                        <Label htmlFor={perm} className="text-sm cursor-pointer">
                          {allPermissions[perm] || perm}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-slate-500">
                {editPermissions.length} permissions selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="bg-indigo-900 hover:bg-indigo-800"
                  data-testid="save-permissions-btn"
                >
                  {saving ? (
                    <ArrowsClockwise size={16} className="mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck size={16} className="mr-2" />
                  )}
                  Save Permissions
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
